import styled from "styled-components";
import { QUERIES, SPACING } from "../../../../utils/constants";
import { StatusProps } from "./types";

/* Mobile: floating rounded bar pinned above the bottom nav, shown only when
   there are unsaved changes, with the status stacked above a two-button row.
   Desktop (tabletLargeAndUp): inline bar that is always visible, status on the
   left and actions on the right. */
export const Bar = styled.div<StatusProps>`
  position: fixed;
  left: ${SPACING.md}px;
  right: ${SPACING.md}px;
  bottom: calc(64px + env(safe-area-inset-bottom, 0px) + ${SPACING.md}px);
  z-index: 1050;
  display: ${(props) => (props.$isDirty ? "flex" : "none")};
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  border-radius: 1rem;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  box-shadow: var(--shadow-large);

  @media ${QUERIES.tabletLargeAndUp} {
    position: static;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACING.md}px;
    padding: ${SPACING.md}px ${SPACING.lg}px;
    border-radius: 0.75rem;
    background: var(--color-background-2);
    box-shadow: none;
  }
`;

/* Mobile-only in-flow spacer. The Bar is fixed (out of flow), so without this
   the floating bar overlaps the last field. Reserves the bar's footprint plus
   the bottom nav + safe area, only while there are unsaved changes. */
export const Spacer = styled.div<StatusProps>`
  display: ${(props) => (props.$isDirty ? "block" : "none")};
  height: calc(64px + env(safe-area-inset-bottom, 0px) + 2rem);

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const Status = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const StatusDot = styled.span<StatusProps>`
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${(props) =>
    props.$isDirty ? "var(--color-warning)" : "var(--color-text-tertiary)"};
`;

export const StatusText = styled.span<StatusProps>`
  /* Mobile: compact uppercase caption (always the "dirty" state here). */
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-warning);

  .label-full {
    display: none;
  }

  .label-short {
    display: inline;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    font-style: italic;
    font-size: var(--text-base);
    font-weight: 400;
    letter-spacing: normal;
    text-transform: none;
    color: ${(props) =>
      props.$isDirty
        ? "var(--color-text-default)"
        : "var(--color-text-secondary)"};

    .label-full {
      display: inline;
    }

    .label-short {
      display: none;
    }
  }
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;

  /* Mobile: full-width row with Save wider than Discard. */
  button {
    flex: 1;
    min-height: 44px;
  }

  button:last-child {
    flex: 2;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    button,
    button:last-child {
      flex: initial;
      min-height: revert;
    }
  }
`;
